import React, { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, Undo2, Redo2, Image as ImageIcon, Save, Loader2, Outdent, Indent, ClipboardPaste, Copy, Scissors, Superscript, Subscript, Highlighter, Link2, Table, Minus as HRIcon, Printer, FileDown, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { dapi as api } from "./deskApi";
import { queueIfOffline } from "@/lib/offlineQueue";
import { formatErr } from "@/lib/api";

const WORD_BLUE = "#2b579a";
const FONTS = ["Calibri", "Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Trebuchet MS", "Cambria"];
const SIZES = [["1", "8"], ["2", "10"], ["3", "11"], ["4", "14"], ["5", "18"], ["6", "24"], ["7", "36"]];
const MARGINS = { Normal: 96, Narrow: 48, Moderate: 72, Wide: 144 };

function RBtn({ onClick, title, children, testid, className = "" }) {
  return (
    <button type="button" title={title} data-testid={testid}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`p-1 rounded-sm hover:bg-[#c8c6c4]/50 hover:border-[#c8c6c4] border border-transparent text-slate-700 ${className}`}>
      {children}
    </button>
  );
}

function Group({ label, children }) {
  return (
    <div className="flex flex-col justify-between px-2 border-r border-[#d1cfcd] shrink-0">
      <div className="flex items-center gap-0.5 flex-1">{children}</div>
      <div className="text-[10px] text-[#605e5c] text-center pt-0.5 pb-0.5">{label}</div>
    </div>
  );
}

const RIBBON_TABS = [["home", "Home"], ["insert", "Insert"], ["layout", "Layout"], ["view", "View"]];

/** Microsoft Word look-alike editor. Saves as .cdoc (HTML) into the file
 * manager; also opens uploaded .docx (converted server-side). */
export default function DocsApp({ file = null }) {
  const edRef = useRef(null);
  const imgRef = useRef(null);
  const [name, setName] = useState("Document1");
  const [nodeId, setNodeId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!file);
  const [tab, setTab] = useState("home");
  const [fileMenu, setFileMenu] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [margin, setMargin] = useState(96);
  const [words, setWords] = useState(0);
  const [pages, setPages] = useState(1);
  const [autoSave, setAutoSave] = useState(() => localStorage.getItem("desk_autosave") === "1");
  const [savedAt, setSavedAt] = useState(null);
  const autoTimer = useRef(null);
  useEffect(() => () => clearTimeout(autoTimer.current), []);

  const recount = () => {
    const t = edRef.current?.innerText || "";
    setWords((t.match(/\S+/g) || []).length);
    setPages(Math.max(1, Math.ceil((edRef.current?.scrollHeight || 1) / 1056)));
  };

  useEffect(() => {
    document.execCommand("styleWithCSS", false, true);
    if (!file) return;
    (async () => {
      try {
        if (/\.docx$/i.test(file.name)) {
          const { data } = await api.post(`/desktop/office/docx-html/${file.id}`);
          edRef.current.innerHTML = data.html || "";
          setName(file.name.replace(/\.docx$/i, ""));
        } else {
          const r = await api.get(`/desktop/fs/${file.id}/download`, { responseType: "blob" });
          edRef.current.innerHTML = await r.data.text();
          setName(file.name.replace(/\.cdoc$/i, ""));
          setNodeId(file.id);
        }
        recount();
      } catch (e) {
        toast.error(formatErr(e.response?.data?.detail) || "Could not open this document.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const cmd = (c, v = null) => { edRef.current?.focus(); document.execCommand(c, false, v); recount(); };

  const insertImage = (f) => {
    if (!f) return;
    const fr = new FileReader();
    fr.onload = () => cmd("insertImage", fr.result);
    fr.readAsDataURL(f);
  };

  const pasteClipboard = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) cmd("insertText", t);
    } catch { toast.info("Press Ctrl+V inside the page to paste."); }
  };

  const insertTable = () => {
    const cell = '<td style="border:1px solid #a0a0a0;padding:6px 10px;min-width:70px"><br/></td>';
    const row = `<tr>${cell.repeat(3)}</tr>`;
    cmd("insertHTML", `<table style="border-collapse:collapse;margin:8px 0">${row.repeat(3)}</table><p><br/></p>`);
  };

  const insertLink = () => {
    const url = window.prompt("Address (URL):", "https://");
    if (url && url !== "https://") cmd("createLink", url);
  };

  const saveErr = (e) =>
    e.response?.data?.detail
      ? formatErr(e.response.data.detail)
      : e.response
        ? `Save failed (HTTP ${e.response.status}). Please try again.`
        : "Network error while saving — check your connection and try again.";

  const save = async (silent = false) => {
    setBusy(true);
    if (!silent) setFileMenu(false);
    try {
      const html = `<!doctype html><html><body>${edRef.current.innerHTML}</body></html>`;
      const b64 = btoa(unescape(encodeURIComponent(html)));
      const payload = {
        name: `${name.trim() || "Document1"}.cdoc`,
        data_b64: b64,
        content_type: "text/html",
        node_id: nodeId,
      };
      try {
        const { data } = await api.post("/desktop/office/save-doc", payload);
        if (!nodeId) setNodeId(data.id);
        if (data.name) setName(data.name.replace(/\.cdoc$/i, ""));
        setSavedAt(Date.now());
        window.dispatchEvent(new CustomEvent("desk-fs-changed"));
        if (!silent) toast.success(data.folder ? `Saved "${data.name || name}" in File Manager → Documents.` : `Saved "${name}".`);
      } catch (e) {
        if (!silent && queueIfOffline(e, { portal: "desk", method: "post", path: "/desktop/office/save-doc", body: payload, label: `Save document "${name}"` })) {
          setSavedAt(Date.now());
          return;
        }
        if (!silent) toast.error(saveErr(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadDocx = async () => {
    setFileMenu(false);
    try {
      const { asBlob } = await import("html-docx-js-typescript");
      const blob = await asBlob(`<!doctype html><html><body>${edRef.current.innerHTML}</body></html>`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${name.trim() || "document"}.docx`;
      a.click();
    } catch (e) { toast.error("Word export failed: " + e.message); }
  };

  const printPdf = () => {
    setFileMenu(false);
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${name}</title><style>body{font-family:Calibri,Arial;margin:40px;}</style></head><body>${edRef.current.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="h-full flex flex-col bg-[#f3f2f1]" data-testid="docs-app">
      {/* Word title bar */}
      <div className="flex items-center gap-1 px-2 h-9 text-white shrink-0" style={{ background: WORD_BLUE }}>
        <div className="w-6 h-6 rounded-sm bg-white/20 grid place-items-center text-[13px] font-bold italic shrink-0">W</div>
        <label className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/15 hover:bg-white/25 cursor-pointer select-none text-[11px] shrink-0" title={nodeId ? "Save automatically after you stop typing" : "AutoSave turns on after the first save"} data-testid="docs-autosave-toggle">
          <input type="checkbox" className="sr-only" checked={autoSave} onChange={(e) => { setAutoSave(e.target.checked); localStorage.setItem("desk_autosave", e.target.checked ? "1" : "0"); }} />
          <span className={`relative w-7 h-3.5 rounded-full transition-colors ${autoSave ? "bg-emerald-400" : "bg-white/40"}`}>
            <span className={`absolute top-[2px] w-2.5 h-2.5 rounded-full bg-white shadow transition-all ${autoSave ? "left-[16px]" : "left-[2px]"}`} />
          </span>
          AutoSave {autoSave ? "On" : "Off"}
        </label>
        <button type="button" title="Save" onClick={() => save()} disabled={busy} className="p-1 rounded hover:bg-white/20 disabled:opacity-50" data-testid="docs-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </button>
        <button type="button" title="Undo" onClick={() => cmd("undo")} className="p-1 rounded hover:bg-white/20" data-testid="docs-undo"><Undo2 className="w-4 h-4" /></button>
        <button type="button" title="Redo" onClick={() => cmd("redo")} className="p-1 rounded hover:bg-white/20" data-testid="docs-redo"><Redo2 className="w-4 h-4" /></button>
        <div className="flex-1 flex items-center justify-center gap-1.5 min-w-0">
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="bg-transparent text-white text-[13px] font-medium text-right outline-none border-b border-transparent focus:border-white/60 max-w-[45%] min-w-0"
            style={{ width: `${Math.max(6, name.length)}ch` }}
            data-testid="docs-name-input" />
          <span className="text-[13px] text-white/85 whitespace-nowrap">- Word</span>
        </div>
        <div className="w-24" />
      </div>

      {/* Ribbon tab strip */}
      <div className="flex items-end gap-0.5 px-1.5 pt-1 text-[13px] text-white shrink-0 relative" style={{ background: WORD_BLUE }}>
        <button type="button" onClick={() => setFileMenu((v) => !v)}
          className="px-4 py-1 font-semibold hover:bg-white/15 rounded-t" data-testid="docs-tab-file">File</button>
        {RIBBON_TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setFileMenu(false); }}
            className={`px-3.5 py-1 rounded-t ${tab === k && !fileMenu ? "bg-[#f3f2f1] text-[#2b579a] font-semibold" : "hover:bg-white/15"}`}
            data-testid={`docs-tab-${k}`}>{l}</button>
        ))}
        {fileMenu && (
          <div className="absolute left-1.5 top-full z-30 bg-white shadow-2xl border border-slate-200 rounded-b-md w-60 py-1 text-slate-800" data-testid="docs-file-menu">
            <button type="button" onClick={() => save()} className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] hover:bg-slate-100"><Save className="w-4 h-4 text-[#2b579a]" /> Save to File Manager</button>
            <button type="button" onClick={downloadDocx} className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] hover:bg-slate-100" data-testid="docs-export-docx"><FileDown className="w-4 h-4 text-[#2b579a]" /> Download as Word (.docx)</button>
            <button type="button" onClick={printPdf} className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] hover:bg-slate-100" data-testid="docs-export-pdf"><Printer className="w-4 h-4 text-[#2b579a]" /> Print / Save as PDF</button>
          </div>
        )}
      </div>

      {/* Ribbon */}
      <div className="flex items-stretch bg-[#f3f2f1] border-b border-[#d1cfcd] px-1 py-1 overflow-x-auto shrink-0 min-h-[84px]">
        {tab === "home" && (
          <>
            <Group label="Clipboard">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={pasteClipboard}
                className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="docs-paste">
                <ClipboardPaste className="w-6 h-6" /><span className="text-[10px]">Paste</span>
              </button>
              <div className="flex flex-col gap-0.5">
                <RBtn title="Cut" onClick={() => cmd("cut")} testid="docs-cut"><Scissors className="w-3.5 h-3.5" /></RBtn>
                <RBtn title="Copy" onClick={() => cmd("copy")} testid="docs-copy"><Copy className="w-3.5 h-3.5" /></RBtn>
              </div>
            </Group>
            <Group label="Font">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  <select onChange={(e) => cmd("fontName", e.target.value)} className="text-xs border border-[#c8c6c4] rounded-sm px-1 py-0.5 w-32 bg-white" data-testid="docs-font">
                    {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select defaultValue="3" onChange={(e) => cmd("fontSize", e.target.value)} className="text-xs border border-[#c8c6c4] rounded-sm px-1 py-0.5 bg-white" data-testid="docs-size">
                    {SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="flex items-center">
                  <RBtn title="Bold (Ctrl+B)" onClick={() => cmd("bold")} testid="docs-bold"><Bold className="w-4 h-4" /></RBtn>
                  <RBtn title="Italic (Ctrl+I)" onClick={() => cmd("italic")} testid="docs-italic"><Italic className="w-4 h-4" /></RBtn>
                  <RBtn title="Underline (Ctrl+U)" onClick={() => cmd("underline")} testid="docs-underline"><Underline className="w-4 h-4" /></RBtn>
                  <RBtn title="Strikethrough" onClick={() => cmd("strikeThrough")} testid="docs-strike"><Strikethrough className="w-4 h-4" /></RBtn>
                  <RBtn title="Subscript" onClick={() => cmd("subscript")} testid="docs-sub"><Subscript className="w-4 h-4" /></RBtn>
                  <RBtn title="Superscript" onClick={() => cmd("superscript")} testid="docs-sup"><Superscript className="w-4 h-4" /></RBtn>
                  <label className="p-1 cursor-pointer flex flex-col items-center" title="Text highlight color">
                    <Highlighter className="w-3.5 h-3.5 text-slate-700" />
                    <input type="color" defaultValue="#ffff00" className="w-4 h-2 border-0 p-0 bg-transparent cursor-pointer" onChange={(e) => cmd("hiliteColor", e.target.value)} data-testid="docs-highlight" />
                  </label>
                  <label className="p-1 cursor-pointer flex flex-col items-center" title="Font color">
                    <span className="text-[11px] font-bold leading-none text-slate-700">A</span>
                    <input type="color" defaultValue="#c00000" className="w-4 h-2 border-0 p-0 bg-transparent cursor-pointer" onChange={(e) => cmd("foreColor", e.target.value)} data-testid="docs-color" />
                  </label>
                </div>
              </div>
            </Group>
            <Group label="Paragraph">
              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  <RBtn title="Bullets" onClick={() => cmd("insertUnorderedList")} testid="docs-ul"><List className="w-4 h-4" /></RBtn>
                  <RBtn title="Numbering" onClick={() => cmd("insertOrderedList")} testid="docs-ol"><ListOrdered className="w-4 h-4" /></RBtn>
                  <RBtn title="Decrease indent" onClick={() => cmd("outdent")} testid="docs-outdent"><Outdent className="w-4 h-4" /></RBtn>
                  <RBtn title="Increase indent" onClick={() => cmd("indent")} testid="docs-indent"><Indent className="w-4 h-4" /></RBtn>
                </div>
                <div className="flex items-center">
                  <RBtn title="Align left" onClick={() => cmd("justifyLeft")} testid="docs-left"><AlignLeft className="w-4 h-4" /></RBtn>
                  <RBtn title="Center" onClick={() => cmd("justifyCenter")} testid="docs-center"><AlignCenter className="w-4 h-4" /></RBtn>
                  <RBtn title="Align right" onClick={() => cmd("justifyRight")} testid="docs-right"><AlignRight className="w-4 h-4" /></RBtn>
                  <RBtn title="Justify" onClick={() => cmd("justifyFull")} testid="docs-justify"><AlignJustify className="w-4 h-4" /></RBtn>
                </div>
              </div>
            </Group>
            <Group label="Styles">
              {[["p", "Normal", "text-[13px]"], ["h1", "Heading 1", "text-[15px] font-semibold text-[#2b579a]"], ["h2", "Heading 2", "text-[13px] font-semibold text-[#2b579a]"], ["h3", "Heading 3", "text-[12px] font-semibold text-[#1e3f6f]"]].map(([v, l, cls]) => (
                <button key={v} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("formatBlock", v)}
                  className={`px-2.5 py-1.5 mx-0.5 bg-white border border-[#c8c6c4] rounded-sm hover:border-[#2b579a] ${cls}`}
                  data-testid={`docs-style-${v}`}>{l}</button>
              ))}
            </Group>
          </>
        )}
        {tab === "insert" && (
          <>
            <Group label="Tables">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertTable} className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="docs-table">
                <Table className="w-6 h-6" /><span className="text-[10px]">Table</span>
              </button>
            </Group>
            <Group label="Illustrations">
              <button type="button" onClick={() => imgRef.current?.click()} className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="docs-image">
                <ImageIcon className="w-6 h-6" /><span className="text-[10px]">Pictures</span>
              </button>
            </Group>
            <Group label="Links">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertLink} className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="docs-link">
                <Link2 className="w-6 h-6" /><span className="text-[10px]">Link</span>
              </button>
            </Group>
            <Group label="Symbols">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("insertHorizontalRule")} className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="docs-hr">
                <HRIcon className="w-6 h-6" /><span className="text-[10px]">Line</span>
              </button>
            </Group>
          </>
        )}
        {tab === "layout" && (
          <Group label="Page Setup">
            {Object.entries(MARGINS).map(([l, px]) => (
              <button key={l} type="button" onClick={() => setMargin(px)}
                className={`px-3 py-2 mx-0.5 rounded-sm border text-[12px] ${margin === px ? "bg-[#cfe0f5] border-[#2b579a] text-[#2b579a] font-semibold" : "bg-white border-[#c8c6c4] hover:border-[#2b579a] text-slate-700"}`}
                data-testid={`docs-margin-${l.toLowerCase()}`}>{l}<div className="text-[10px] text-[#605e5c] font-normal">{(px / 96).toFixed(2)}"</div></button>
            ))}
          </Group>
        )}
        {tab === "view" && (
          <Group label="Zoom">
            {[50, 75, 100, 125, 150, 200].map((z) => (
              <button key={z} type="button" onClick={() => setZoom(z)}
                className={`px-3 py-2 mx-0.5 rounded-sm border text-[12px] ${zoom === z ? "bg-[#cfe0f5] border-[#2b579a] text-[#2b579a] font-semibold" : "bg-white border-[#c8c6c4] hover:border-[#2b579a] text-slate-700"}`}
                data-testid={`docs-zoom-${z}`}>{z}%</button>
            ))}
          </Group>
        )}
        <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => { insertImage(e.target.files?.[0]); e.target.value = ""; }} />
      </div>

      {/* Page canvas */}
      <div className="flex-1 overflow-auto py-7 bg-[#e0e2e6]" onClick={() => setFileMenu(false)}>
        {loading && <p className="text-xs text-slate-500 text-center">Opening…</p>}
        <div style={{ zoom: zoom / 100 }}>
          <div
            ref={edRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => {
              recount();
              if (autoSave && nodeId) {
                clearTimeout(autoTimer.current);
                autoTimer.current = setTimeout(() => save(true), 1500);
              }
            }}
            className="mx-auto bg-white min-h-[1056px] w-[816px] max-w-full outline-none text-[15px] leading-relaxed"
            style={{ fontFamily: "Calibri, Arial, sans-serif", padding: margin, boxShadow: "0 2px 10px rgba(0,0,0,0.25)" }}
            data-testid="docs-editor"
          />
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between h-6 px-3 text-[11px] text-white shrink-0" style={{ background: WORD_BLUE }}>
        <div className="flex items-center gap-4">
          <span data-testid="docs-page-count">Page 1 of {pages}</span>
          <span data-testid="docs-word-count">{words} words</span>
          {savedAt && <span className="text-emerald-200" data-testid="docs-saved-badge">Saved ✓</span>}
          <span className="hidden sm:inline">English (United States)</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="px-1 hover:bg-white/20 rounded" onClick={() => setZoom((z) => Math.max(50, z - 10))}>−</button>
          <input type="range" min="50" max="200" step="10" value={zoom} onChange={(e) => setZoom(+e.target.value)} className="w-24 accent-white" data-testid="docs-zoom-slider" />
          <button type="button" className="px-1 hover:bg-white/20 rounded" onClick={() => setZoom((z) => Math.min(200, z + 10))}>+</button>
          <span className="w-9 text-right">{zoom}%</span>
        </div>
      </div>
    </div>
  );
}
